function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { Kind } from "graphql";
import { ROOT_QUERY_INCLUDE_VARIABLE_NAME } from "./constants";
import { isRootLevelIncludeDirective } from "./rootQueryInclude";
import { findEntryPointDefinition } from "../documentUtils/entrypoint";
import { getFragmentDefinitions } from "../documentUtils/fragment";
import { createQueryDocument } from "../documentUtils/query";
import { getVariableName } from "../documentUtils/variable";
import { extractPrefixFromVariableName, removePrefixFromCompositionName } from "../../queryUtils/prefixes";
const checkIfDirectivesAreValid = (directives)=>{
    const hasRootLevelInclude = directives.some(isRootLevelIncludeDirective);
    if (!hasRootLevelInclude) {
        throw new Error('Top level field is missing include directive with rootQueryInclude argument');
    }
};
export const removePrefixFromNameNode = (nameNode)=>{
    return {
        kind: Kind.NAME,
        value: removePrefixFromCompositionName(nameNode.value)
    };
};
const removePrefixFromArgumentNodes = (argumentNodes)=>{
    if (typeof argumentNodes === 'undefined') {
        return undefined;
    }
    return argumentNodes.map((argumentNode)=>{
        if (argumentNode.value.kind !== Kind.VARIABLE) {
            return argumentNode;
        }
        return _object_spread_props(_object_spread({}, argumentNode), {
            value: {
                kind: Kind.VARIABLE,
                name: removePrefixFromNameNode(argumentNode.value.name)
            }
        });
    });
};
const removePrefixFromDirectiveNodes = (directiveNodes)=>directiveNodes.map((directive)=>{
        if (directive.name.value !== 'include') {
            return directive;
        }
        if (typeof directive.arguments === 'undefined') {
            return directive;
        }
        return _object_spread_props(_object_spread({}, directive), {
            arguments: removePrefixFromArgumentNodes(directive.arguments)
        });
    });
const removePrefixFromVariableDefinitions = (variableDefinitions)=>variableDefinitions.map((varDef)=>_object_spread_props(_object_spread({}, varDef), {
            variable: _object_spread_props(_object_spread({}, varDef.variable), {
                name: removePrefixFromNameNode(varDef.variable.name)
            })
        }));
function processTopLevelFieldNode(node) {
    const nodeName = node.name.value;
    const { directives } = node;
    if (!directives) {
        throw new Error(`Top level field ${nodeName} is missing include directive with rootQueryInclude argument`);
    }
    checkIfDirectivesAreValid(directives);
    if (!node.alias) {
        throw new Error(`Top level field ${nodeName} does not have alias`);
    }
    const nodeAlias = node.alias;
    const directivesWithoutRootQueryInclude = directives.filter((dir)=>!isRootLevelIncludeDirective(dir));
    return {
        node: {
            kind: Kind.FIELD,
            name: node.name,
            alias: removePrefixFromNameNode(nodeAlias),
            arguments: removePrefixFromArgumentNodes(node.arguments),
            directives: removePrefixFromDirectiveNodes(directivesWithoutRootQueryInclude),
            selectionSet: node.selectionSet
        },
        alias: nodeAlias.value
    };
}
// This name is not used anywhere (e.g. doesn't matter for hashing), but it's useful for debugging
function getSubQueryDebugName(topLevelFields) {
    const fieldNames = topLevelFields.map(({ node })=>node.name.value);
    return `SubQuery_${fieldNames.join('___')}`;
}
function buildSubQueryDocument(topLevelFields, fragmentDefinitions, rootVariablesUsedInSubQuery) {
    const subQueryName = getSubQueryDebugName(topLevelFields);
    const variableDefinitions = removePrefixFromVariableDefinitions(rootVariablesUsedInSubQuery);
    const topLevelFieldNodes = topLevelFields.map(({ node })=>node);
    return createQueryDocument(subQueryName, variableDefinitions, topLevelFieldNodes, fragmentDefinitions);
}
function groupTopLevelFieldsByPrefix(selections) {
    const fieldsByPrefix = {};
    selections.forEach((selectionNode)=>{
        if (selectionNode.kind !== Kind.FIELD) {
            throw new Error(`Composition query cannot have fields of kind ${selectionNode.kind}`);
        }
        const fieldSelection = processTopLevelFieldNode(selectionNode);
        const prefix = extractPrefixFromVariableName(fieldSelection.alias);
        if (!fieldsByPrefix.hasOwnProperty(prefix)) {
            fieldsByPrefix[prefix] = [];
        }
        fieldsByPrefix[prefix].push(fieldSelection);
    });
    return fieldsByPrefix;
}
function groupVariableDefinitionsByPrefix(variableDefinitions) {
    const variablesByPrefix = {};
    variableDefinitions.forEach((varDef)=>{
        const variableName = varDef.variable.name.value;
        if (variableName.includes(ROOT_QUERY_INCLUDE_VARIABLE_NAME)) {
            return;
        }
        const prefix = extractPrefixFromVariableName(variableName);
        if (!variablesByPrefix.hasOwnProperty(prefix)) {
            variablesByPrefix[prefix] = [];
        }
        variablesByPrefix[prefix].push(varDef);
    });
    return variablesByPrefix;
}
export default function splitComposedQueryDocIntoSubQueries(composedQuery) {
    const rootQueryDefinitionNode = findEntryPointDefinition(composedQuery);
    const fieldsByPrefix = groupTopLevelFieldsByPrefix(rootQueryDefinitionNode.selectionSet.selections);
    const variablesByPrefix = groupVariableDefinitionsByPrefix(rootQueryDefinitionNode.variableDefinitions || []);
    const fragmentDefinitions = getFragmentDefinitions(composedQuery);
    const subQueriesPerPrefix = {};
    Object.entries(fieldsByPrefix).forEach(([prefix, topLevelFields])=>{
        const variableDefinitions = variablesByPrefix[prefix] || [];
        const variableNames = variableDefinitions.map(getVariableName);
        const subQuery = buildSubQueryDocument(topLevelFields, fragmentDefinitions, variableDefinitions);
        subQueriesPerPrefix[prefix] = {
            variableNames,
            subQuery
        };
    });
    return subQueriesPerPrefix;
}

//# sourceMappingURL=splitComposedQueryDocIntoSubQueries.js.map